import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './ui';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

/* ============================================================
   DashboardDrill — in-page drilldown surface for the Dashboard.

   A drill is a stack of frames. Each frame carries the accumulated
   filters plus the remaining breakdown dimensions (`chain`). The top
   frame is rendered here:
     - chain non-empty  -> clickable breakdown bars (drill one more level)
     - chain empty      -> Graph + Table of the underlying audit records
   Every frame maps to a one-line query the existing /admin/audit-logs
   parser already understands (status/service/tool_name + earliest/latest).
   ============================================================ */

export type DrillField = 'status' | 'service' | 'tool_name' | 'agent_id';

export interface DrillFilter {
  field: DrillField;
  value: string;
  label: string;
}

export interface DrillFrame {
  /** Accumulated AND-ed filters. */
  filters: DrillFilter[];
  /** Breakdown dimensions still to drill through; chain[0] is the current one. */
  chain: DrillField[];
  /** Only set for time-scoped (Request Trend) drills. */
  timeWindow?: { earliest: string; latest?: string };
  /** Breadcrumb label for THIS frame. */
  crumb: string;
}

interface AuditRecord {
  id: number;
  timestamp: string;
  service: string;
  tool_name: string;
  status: string;
  result: string;
  agent_id: string;
  response_status?: string;
  arguments?: string;
  device_hostname?: string;
  display_name?: string;
}

interface SearchResponse {
  logs: AuditRecord[];
  total: number;
  summary: { total: number; success: number; errors: number; violations: number; uniqueAgents: number; uniqueTools: number };
  timeline: { success: number; errors: number }[];
  facet?: { key: string; count: number }[];
}

interface DashboardDrillProps {
  apiUrl: string;
  stack: DrillFrame[];
  /** Push a new frame onto the stack (drill deeper). */
  onPush: (frame: DrillFrame) => void;
  /** Truncate the stack to `len` frames (0 = exit to the dashboard grid). */
  onPopTo: (len: number) => void;
}

const PAGE_SIZE = 25;

const FIELD_LABEL: Record<DrillField, string> = {
  status: 'Outcome',
  service: 'Service',
  tool_name: 'Tool',
  agent_id: 'Agent',
};

const titleize = (s: string): string =>
  (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const statusColor = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (s === 'success' || s === 'policy_allowed' || s === 'policy_advisory_allow' || s === 'proxied' || s === 'accepted') return 'var(--success)';
  if (s === 'error' || s === 'upstream_error') return 'var(--danger)';
  if (s.includes('den') || s.includes('block')) return 'var(--danger)';
  if (s.includes('warn') || s.includes('anomal') || s.includes('flag')) return 'var(--warning)';
  return 'var(--accent)';
};

const statusBadgeClass = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (s === 'success' || s === 'policy_allowed' || s === 'proxied' || s === 'accepted') return 'allow';
  if (s === 'error' || s === 'upstream_error' || s.includes('den') || s.includes('block')) return 'deny';
  if (s.includes('warn') || s.includes('anomal') || s.includes('flag')) return 'warn';
  return 'neutral';
};

/** Turn a frame into a Splunk-style query the audit parser understands. */
export const buildQuery = (frame: DrillFrame): string => {
  const parts = frame.filters.map((f) => {
    const v = /\s/.test(f.value) ? `"${f.value}"` : f.value;
    return `${f.field}=${v}`;
  });
  if (frame.timeWindow) {
    parts.push(`earliest=${frame.timeWindow.earliest}`);
    if (frame.timeWindow.latest) parts.push(`latest=${frame.timeWindow.latest}`);
  }
  return parts.join(' ') || 'earliest=90d';
};

const DashboardDrill: React.FC<DashboardDrillProps> = ({ apiUrl, stack, onPush, onPopTo }) => {
  const { t } = useTranslation();
  const top = stack[stack.length - 1];
  const breakdown: DrillField | null = top.chain.length > 0 ? top.chain[0] : null;
  const isLeaf = breakdown === null;

  const [data, setData] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);

  // A stable key that changes whenever the frame's meaning changes.
  const frameKey = JSON.stringify({ f: top.filters, b: breakdown, t: top.timeWindow });
  const query = buildQuery(top);

  const fetchData = useCallback(async (off: number) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ q: query, limit: String(PAGE_SIZE), offset: String(off), sort: 'DESC' });
      if (breakdown) params.append('facet', breakdown);
      const resp = await fetch(`${apiUrl}/admin/audit-logs/search?${params}`);
      const json = await resp.json();
      if (!resp.ok) {
        setError(json.error || 'Query failed');
        setData(null);
        return;
      }
      setError('');
      setData(json);
    } catch (err) {
      setError('Could not reach the firewall API.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, query, breakdown]);

  // Reset paging and refetch whenever the active frame changes.
  useEffect(() => {
    setOffset(0);
    fetchData(0);
  }, [frameKey, fetchData]);

  const goPage = (off: number) => {
    setOffset(off);
    fetchData(off);
  };

  // ── Breadcrumb ──
  const crumbs = (
    <div className="db-drill-crumbs">
      <button className="db-crumb db-crumb-root" onClick={() => onPopTo(0)}>
        <Icon name="dashboard" size={13} /> {t('components.dashboardDrill.dashboard')}
      </button>
      {stack.map((f, i) => (
        <React.Fragment key={i}>
          <span className="db-crumb-sep">/</span>
          <button
            className={`db-crumb${i === stack.length - 1 ? ' db-crumb-active' : ''}`}
            onClick={() => onPopTo(i + 1)}
            disabled={i === stack.length - 1}
          >
            {f.crumb}
          </button>
        </React.Fragment>
      ))}
    </div>
  );

  const filterSummary = top.filters.map((f) => `${FIELD_LABEL[f.field]}: ${f.label}`).join('  ·  ');
  const total = data?.total ?? 0;

  // ── Header title ──
  const title = isLeaf
    ? 'Matching Records'
    : `Break down by ${FIELD_LABEL[breakdown!]}`;

  return (
    <div className="db-drill">
      <div className="db-drill-topbar">
        <button className="ent-btn ghost sm db-drill-back" onClick={() => onPopTo(stack.length - 1)}>
          {t('components.dashboardDrill.back')}
        </button>
        {crumbs}
      </div>

      <div className="db-card db-drill-card">
        <div className="db-card-head">
          <div>
            <h3>{title}</h3>
            {filterSummary && <p className="db-drill-filter">{filterSummary}{top.timeWindow ? `  ·  ${t('components.dashboardDrill.windowed')}` : ''}</p>}
          </div>
          {!loading && !error && <span className="db-badge">{t('components.dashboardDrill.recordsCount', { count: total })}</span>}
        </div>

        {error && <div className="db-drill-error"><Icon name="warning" size={14} /> {error}</div>}

        {loading ? (
          <div className="db-empty"><Icon name="refresh" /><p>{t('components.dashboardDrill.loading')}</p></div>
        ) : error ? null : isLeaf ? (
          <LeafView data={data} offset={offset} onPage={goPage} query={query} />
        ) : (
          <BreakdownView
            data={data}
            field={breakdown!}
            onDrill={(key) => {
              const nextChain = top.chain.slice(1);
              onPush({
                filters: [...top.filters, { field: breakdown!, value: key, label: breakdown === 'status' ? titleize(key) : key }],
                chain: nextChain,
                timeWindow: top.timeWindow,
                crumb: breakdown === 'status' ? titleize(key) : key,
              });
            }}
            onViewRecords={() => {
              onPush({
                filters: top.filters,
                chain: [],
                timeWindow: top.timeWindow,
                crumb: 'Records',
              });
            }}
          />
        )}
      </div>
    </div>
  );
};

/* ── Intermediate level: clickable breakdown bars ── */
const BreakdownView: React.FC<{
  data: SearchResponse | null;
  field: DrillField;
  onDrill: (key: string) => void;
  onViewRecords: () => void;
}> = ({ data, field, onDrill, onViewRecords }) => {
  const { t } = useTranslation();
  const facet = data?.facet || [];
  const grandTotal = data?.total || 0;
  if (facet.length === 0) {
    return <div className="db-empty"><Icon name="checkCircle" /><p>{t('components.dashboardDrill.noRecordsMatchThisSelection')}</p></div>;
  }
  const maxCount = Math.max(...facet.map((f) => f.count), 1);
  return (
    <>
      <div className="db-bar-list db-drill-breakdown">
        {facet.map((f) => {
          const pct = grandTotal ? Math.round((f.count / grandTotal) * 100) : 0;
          const label = field === 'status' ? titleize(f.key) : f.key;
          const color = field === 'status' ? statusColor(f.key) : 'var(--accent)';
          return (
            <div
              key={f.key}
              className="db-bar-row db-drill-bar"
              role="button"
              tabIndex={0}
              onClick={() => onDrill(f.key)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDrill(f.key); } }}
              title={t('components.dashboardDrill.drillInto', { name: label })}
            >
              <div className="db-bar-label">
                <span className="db-bar-name">{label}</span>
                <span className="db-bar-count">{f.count} <span className="db-bar-pct">({pct}%)</span><Icon name="chevronRight" size={13} /></span>
              </div>
              <div className="db-bar-track">
                <div className="db-bar-fill" style={{ width: `${(f.count / maxCount) * 100}%`, background: color }} />
              </div>
            </div>
          );
        })}
      </div>
      <button className="ent-btn sm db-drill-viewall" onClick={onViewRecords}>
        <Icon name="logs" size={14} /> {t('components.dashboardDrill.viewAllRecords', { count: grandTotal })}
      </button>
    </>
  );
};

/* ── Leaf level: Graph (timeline + summary) + Table of records ── */
const LeafView: React.FC<{
  data: SearchResponse | null;
  offset: number;
  onPage: (off: number) => void;
  query: string;
}> = ({ data, offset, onPage, query }) => {
  const { t: tr } = useTranslation();
  const [expanded, setExpanded] = useState<number | null>(null);
  const logs = data?.logs || [];
  const total = data?.total || 0;
  const summary = data?.summary || { total: 0, success: 0, errors: 0, violations: 0, uniqueAgents: 0, uniqueTools: 0 };
  const timeline = data?.timeline && data.timeline.length > 0 ? data.timeline : Array(24).fill({ success: 0, errors: 0 });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const maxVal = Math.max(...timeline.map((b) => (b.success || 0) + (b.errors || 0)), 1);

  return (
    <>
      {/* Summary tiles */}
      <div className="db-drill-summary">
        <div className="db-drill-stat"><span className="db-drill-stat-num">{summary.total}</span><span className="db-drill-stat-lbl">{tr('components.dashboardDrill.total')}</span></div>
        <div className="db-drill-stat"><span className="db-drill-stat-num" style={{ color: 'var(--success)' }}>{summary.success}</span><span className="db-drill-stat-lbl">{tr('components.dashboardDrill.success')}</span></div>
        <div className="db-drill-stat"><span className="db-drill-stat-num" style={{ color: 'var(--danger)' }}>{summary.violations}</span><span className="db-drill-stat-lbl">{tr('components.dashboardDrill.violations')}</span></div>
        <div className="db-drill-stat"><span className="db-drill-stat-num">{summary.uniqueAgents}</span><span className="db-drill-stat-lbl">{tr('components.dashboardDrill.agents')}</span></div>
        <div className="db-drill-stat"><span className="db-drill-stat-num">{summary.uniqueTools}</span><span className="db-drill-stat-lbl">{tr('components.dashboardDrill.tools')}</span></div>
      </div>

      {/* Timeline graph (last 24h) */}
      <div className="db-drill-timeline">
        <div className="db-drill-timeline-head">{tr('components.dashboardDrill.activityLast24h')}</div>
        <div className="db-drill-tl-chart">
          {timeline.map((b, i) => {
            const t = (b.success || 0) + (b.errors || 0);
            const h = Math.max(2, (t / maxVal) * 52);
            const successH = t > 0 ? (b.success / t) * h : 0;
            const errorH = t > 0 ? (b.errors / t) * h : 0;
            return (
              <div key={i} className="db-drill-tl-col" title={tr('components.dashboardDrill.hourBucket', { hour: i, success: b.success, errors: b.errors })}>
                <div className="db-drill-tl-stack" style={{ height: `${h}px` }}>
                  {errorH > 0 && <div className="db-drill-tl-bar" style={{ height: `${errorH}px`, background: 'var(--danger)' }} />}
                  {successH > 0 && <div className="db-drill-tl-bar" style={{ height: `${successH}px`, background: 'var(--success)' }} />}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Records table */}
      {logs.length > 0 ? (
        <>
          <div className="ent-table-wrap scroll db-drill-records">
            <table className="ent-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>{tr('components.dashboardDrill.timestamp')}</th>
                  <th>{tr('components.dashboardDrill.service')}</th>
                  <th>{tr('components.dashboardDrill.tool')}</th>
                  <th>{tr('components.dashboardDrill.agentDevice')}</th>
                  <th>{tr('components.dashboardDrill.status')}</th>
                  <th>{tr('components.dashboardDrill.result')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const open = expanded === log.id;
                  return (
                    <React.Fragment key={log.id}>
                      <tr className="db-drill-row" onClick={() => setExpanded(open ? null : log.id)}>
                        <td>{log.id}</td>
                        <td>{parseServerDate(log.timestamp).toLocaleString()}</td>
                        <td>{log.service}</td>
                        <td>{titleize(log.tool_name || '-')}</td>
                        <td title={log.agent_id}>{log.device_hostname || log.display_name || log.agent_id || '-'}</td>
                        <td><span className={`db-pill db-pill-${statusBadgeClass(log.status)}`}>{titleize(log.status)}</span></td>
                        <td className="db-drill-result" title={log.result}>{log.result && log.result.length > 60 ? log.result.slice(0, 60) + '…' : (log.result || '-')}</td>
                      </tr>
                      {open && (
                        <tr className="db-drill-detail-row">
                          <td colSpan={7}>
                            <div className="db-drill-detail">
                              <div><strong>{tr('components.dashboardDrill.agent')}</strong> {log.agent_id || '-'}</div>
                              <div><strong>{tr('components.dashboardDrill.http')}</strong> {log.response_status || '-'}</div>
                              <div className="db-drill-detail-full"><strong>{tr('components.dashboardDrill.result2')}</strong> {log.result || tr('components.dashboardDrill.noDetails')}</div>
                              {log.arguments && <div className="db-drill-detail-full"><strong>{tr('components.dashboardDrill.arguments')}</strong> <pre>{log.arguments}</pre></div>}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="db-drill-pager">
            <span className="db-drill-page-info">{tr('components.dashboardDrill.showingOf', { v0: total === 0 ? 0 : offset + 1, v1: Math.min(offset + PAGE_SIZE, total), v2: total })}</span>
            <div className="db-drill-page-controls">
              <button className="ent-btn sm" onClick={() => onPage(Math.max(0, offset - PAGE_SIZE))} disabled={currentPage === 1}>{tr('components.dashboardDrill.prev')}</button>
              <span className="db-drill-page-num">{tr('components.dashboardDrill.page', { v0: currentPage, v1: totalPages })}</span>
              <button className="ent-btn sm" onClick={() => onPage(offset + PAGE_SIZE)} disabled={currentPage >= totalPages}>{tr('components.dashboardDrill.next')}</button>
              <Link className="ent-btn sm" to={`/audit-logs?q=${encodeURIComponent(query)}`} title={tr('components.dashboardDrill.openThisQueryInAuditLogs')}>
                <Icon name="logs" size={13} /> {tr('components.dashboardDrill.openInAuditLogs')}
              </Link>
            </div>
          </div>
        </>
      ) : (
        <div className="db-empty"><Icon name="checkCircle" /><p>{tr('components.dashboardDrill.noRecordsMatchThisSelection')}</p></div>
      )}
    </>
  );
};

export default DashboardDrill;
